/**
 * Remembers which manual account the user is linking while they are away at
 * their bank.
 *
 * Connecting a bank is a full navigation to the Powens webview and back, so any
 * in-memory state is gone by the time the callback page loads. The intent only
 * steers where the user lands afterwards — the link itself is a separate,
 * confirmed step — so a lost or stale intent costs a click, never data.
 */

const KEY = 'clevia-powens-link-intent';

/** Past this, the user has clearly moved on: do not hijack their next visit. */
const MAX_AGE_MS = 60 * 60 * 1000;

interface LinkIntent {
  accountId: string;
  at: number;
}

export function saveLinkIntent(accountId: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ accountId, at: Date.now() } satisfies LinkIntent));
  } catch {
    // Storage unavailable (private mode): the user relinks from the menu.
  }
}

export function readLinkIntent(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;

    const intent = JSON.parse(raw) as Partial<LinkIntent>;
    if (typeof intent.accountId !== 'string' || typeof intent.at !== 'number') return null;
    if (Date.now() - intent.at > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }

    return intent.accountId;
  } catch {
    return null;
  }
}

export function clearLinkIntent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}
